import React, {
  memo, useState, useMemo, useCallback, useEffect,
} from 'react'
import Image from 'next/legacy/image'
import clsx from 'clsx'
import { FormattedMessage, useIntl } from 'react-intl'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import NumberFormat from 'react-number-format'
import Grid from '@mui/material/Grid2'
import { useDispatch } from 'react-redux'
import {
  formatStateToShortName,
  getRandomPeopleRequestingCredit,
  getRandomPeopleWatchingCard,
} from 'utils/miscellaneous'
import {
  getVehicleStatus,
  SHOW_INCLUDED_WARRANTY,
  getVehicleDiscounts,
  getVehicleTitle,
  getVehicleType,
  getBranchName,
  getVehicleWarrantyConfig,
} from 'utils/vehicles'
import { setValue } from 'redux/modules/search'
import Box from '@mui/material/Box'
import usePageProps from 'hooks/usePageProps'
import NoImage from 'public/img/No_foto_AutoSRP.webp'
import Text, { TEXT_TYPES } from 'components/designSystem/Text'
import NoVehicleImage from 'public/img/Nofotos_proximo.jpeg'
import LocationIcon from 'components/icons/location.svg'
import { titleCaps } from 'utils/universal'
import BranchIcon from 'components/icons/branchIcon.svg'
import { EASY_TYPE, PARTICULAR_TYPE, VDP_TYPE } from 'enums'
import DicountBanner from 'components/VDP/DicountBanner'
import CardLink from 'components/SRP/CardLink'
import FlameAnimation from 'components/SRP/FlameAnimation'
import { applyThousandSeparator } from 'react-number-format/lib/utils'
// import useFinancing, { getMinMaxDeposit } from 'hooks/useFinancing'
// import envs from 'envs'
import sx from './styles'

const VehicleMobileCardVariant = (props) => {
  const {
    vehicle,
    className: classNameProp = '',
    direction = 'column',
    href,
    sx: sxProp = {},
    priority = false,
    // credit,
    // goodCreditHistory,
    // isB2BVehicle = false,
  } = props
  const isProximo = vehicle.status === 'ONGOING'
  const {
    price, precioTachado, hasDiscount,
  } = useMemo(() => getVehicleDiscounts(vehicle), [vehicle])
  const { temporaryImageDomain, quality = 60 } = usePageProps()
  const { formatMessage: f } = useIntl()
  const dispatch = useDispatch()
  const [showCreditPersons, setShowCreditPersons] = useState(null)
  const [randomPeopleRequestingCredit, setRandomPeopleRequestCredit] = useState(null)
  const [randomPeopleWatchingCard, setRandomPeopleWatchingCard] = useState(null)
  const vehicleType = getVehicleType(vehicle)
  const variant = '3'
  const isClikAutoVDP = vehicleType === VDP_TYPE.CLIKAUTO
  const isAgencyVDP = vehicleType === VDP_TYPE.AGENCIA || vehicleType === VDP_TYPE.PARTNER
  const showHitchFrom = (
    (price && (isClikAutoVDP || isAgencyVDP) && vehicle?.year > 2013)
    || (price
      && vehicleType === VDP_TYPE.PARTICULAR
      && (vehicle?.vehicleInspection?.acceptFinancing || vehicle?.acceptFinancing)
      && vehicle?.year > 2013)
  )

  // const autoWarranty = (vehicle?.classWarranty && (isAgencyVDP || isClikAutoVDP))
  const consignaVirtual = vehicle?.easy && (vehicle?.holdingType === 'virtual')
  const consignaExist = !(vehicle?.easy && vehicle?.holdingType === null)
  const isAutocom = vehicle?.ownerType === EASY_TYPE.AGENCIA
          && vehicle?.holdingType === PARTICULAR_TYPE.VIRTUAL

  const className = useMemo(() => clsx('VehicleCard-root', classNameProp, direction), [classNameProp, direction])
  const titleCar = useMemo(() => getVehicleTitle(vehicle), [vehicle])
  const vehicleStatus = useMemo(() => getVehicleStatus(vehicle), [vehicle])

  /**
   * Un auto vendido no lleva a ningún lado: su VDP redirige al listado. Se
   * desactiva el enlace y el handler de clic, que además dispara estado de
   * Redux pensado para la ficha de detalle.
   */
  const isSold = vehicleStatus === 'ARCHIVED'

  const showCreditText = (!vehicle?.consignacion)
    || (
      vehicle?.consignacion && (
        vehicle?.acceptFinancing || vehicle?.vehicleInspection?.acceptFinancing)
    )
    || (!vehicle.authorizedSeller)
  const state = vehicleType === VDP_TYPE.PARTICULAR ? vehicle?.branch?.state : vehicle.state

  const { showOneYearWarranty, showThreeMonthWarranty } = getVehicleWarrantyConfig(vehicle)

  const showGoBackTag = showOneYearWarranty

  // const {
  //   defaultDeposit,
  // } = getMinMaxDeposit(
  //   price,
  //   vehicle?.year,
  // )
  // const {
  //   financingOptions,
  // } = useFinancing(
  //   {
  //     price,
  //     year: vehicle.year,
  //   },
  //   defaultDeposit,
  //   credit,
  //   goodCreditHistory,
  // )

  const noImageSlide = (
    <Image
      src={NoVehicleImage}
      layout="fill"
      objectFit="cover"
      placeholder="blur"
      alt="Próximo Auto"
      title="Próximo Auto"
      unoptimized
    />
  )

  const renderImage = useCallback((image) => {
    return (
      <>
        {hasDiscount && (
          <Box sx={sx.hasDiscountContainer}>
            <Typography
              variant="quote"
              sx={sx.hasDiscountText}
            >
              <FormattedMessage id="VEHICLE_STATUS_WITH_DISCOUNT" />
            </Typography>
            <FlameAnimation size={44} />
          </Box>
        )}
        {image ? (
          <Image
            src={`${temporaryImageDomain}/300x200/filters:format(webp)/filters:quality(${quality})/${image.filename}`}
            blurDataURL={`${temporaryImageDomain}/10x6/filters:blur(90)/filters:format(webp)/filters:quality(${quality})/${image.filename}`}
            placeholder="blur"
            layout="fill"
            objectFit="cover"
            priority={priority}
            title={`Autos seminuevos y autos usados ${titleCar} al mejor precio en ClikAuto`}
            alt={`Autos seminuevos, ${titleCar}`}
            unoptimized
          />
        ) : (
          <Image
            src={NoImage}
            objectFit="contain"
            layout="fill"
            alt="Default photo"
            title={`Autos seminuevos, ${titleCar}`}
          />
        )}
      </>
    )
  }, [titleCar, vehicle])

  const holdFrame = useMemo(() => (
    <Box sx={sx.hold}>
      {
        isProximo ? (
          noImageSlide
        ) : (
          <Typography variant="body1">
            {titleCaps(f({ id: 'VEHICLE_STATUS_HOLD' }))}
          </Typography>
        )
      }
    </Box>
  ), [])

  const profilePhoto = useMemo(() => (
    <Box
      sx={{
        width: '100%',
        position: 'relative',
        height: (variant || vehicleType === 'clikauto') ? '100%' : 100,
        minHeight: 156,
      }}
    >
      {renderImage(vehicle?.images?.[0])}
      {(vehicleStatus === 'HOLD' || vehicleStatus === 'HOLD_ONLINE' || isProximo) && holdFrame}
    </Box>
  ), [vehicle])

  // const renderMountlyPayment = ({ payment }) => (
  //   <Box>
  //     <Typography
  //       sx={sx.credit}
  //       variant="body1"
  //     >
  //       <FormattedMessage
  //         id="PRICE_FROM"
  //         values={{
  //           monthlyInstallment: applyThousandSeparator(`$${payment}`, ','),
  //           span: (chunks) => <span>{chunks}</span>,
  //         }}
  //       />
  //     </Typography>
  //   </Box>
  // )

  const hitchFrom = ({ hitchAmount }) => (
    <Box>
      <Typography
        sx={sx.credit}
        variant="caption"
      >
        <Typography>
          <FormattedMessage
            id="HITCH_FROM"
            values={{
              monthlyInstallment: applyThousandSeparator(`$${Math.ceil(hitchAmount / 1000) * 1000}`, ','),
              span: (chunks) => <span>{chunks}</span>,
            }}
          />
        </Typography>
      </Typography>
    </Box>
  )

  const handleOnClickItem = useCallback(() => {
    dispatch(setValue({
      key: 'userViews',
      value: showCreditText && showCreditPersons
        ? randomPeopleRequestingCredit
        : randomPeopleWatchingCard,
      persist: true,
    }))
    dispatch(setValue({
      key: 'showCreditPersons',
      value: showCreditText && showCreditPersons,
      persist: true,
    }))
  }, [showCreditPersons, randomPeopleRequestingCredit, randomPeopleWatchingCard])

  useEffect(() => {
    setShowCreditPersons(Math.floor(Math.random() * 2) === 0)
    setRandomPeopleRequestCredit(getRandomPeopleRequestingCredit())
    setRandomPeopleWatchingCard(getRandomPeopleWatchingCard())
  }, [])

  return (
    <CardLink
      disabled={isSold}
      href={href}
    >
      <Paper
        sx={{ ...sx.root, ...sxProp, ...(isSold ? { cursor: 'default' } : {}) }}
        className={clsx(className, { hasDiscount })}
        elevation={0}
        onClick={isSold ? undefined : handleOnClickItem}
        title={`Autos seminuevos, ${titleCar}`}
      >

        <Grid container>
          <Grid
            size={direction === 'row' ? 4 : 12}
          >
            {profilePhoto}
          </Grid>
          <Grid
            size={direction === 'row' ? 8 : 12}
            p={1}
            container
            sx={{ border: '1px solid #E9E9E9' }}
          >
            {
              isProximo && (
                <DicountBanner
                  text="VEHICLE_STATUS_COMMING_SOON"
                  position="relative"
                  top="default"
                  right="default"
                  bgcolor="#135FC4"
                />
              )
            }

            {/* Oculta por el interruptor de garantía incluida. */}
            {(SHOW_INCLUDED_WARRANTY && showOneYearWarranty) && (
              <DicountBanner
                text="VEHICLE_STATUS_ONE_YEAR"
                position="relative"
                top="default"
                right="default"
                bgcolor="#8C46ED"
              />
            )}

            {/* Oculta por el interruptor de garantía incluida. */}
            {(SHOW_INCLUDED_WARRANTY && showThreeMonthWarranty) && (
              <DicountBanner
                text="VEHICLE_STATUS_THREE_MONTHS"
                position="relative"
                top="default"
                right="default"
                bgcolor="#8C46ED"
              />
            )}

            {(showGoBackTag) && (
              <DicountBanner
                text="VEHICLE_STATUS_GO_BACK"
                position="relative"
                top="default"
                right="default"
                bgcolor="#09C7A6"
              />
            )}

            <Grid size={12}>
              <Typography
                variant="h3"
                component="h2"
                gutterBottom
                sx={sx.titleCar}
              >
                {titleCar}
              </Typography>
            </Grid>
            <Grid
              size={12}
              sx={sx.priceContainer}
            >
              <Text
                type={TEXT_TYPES.heading.h4}
                component="h3"
                sx={sx.price(hasDiscount ? 'common.purple' : 'text.magenta')}
              >

                <NumberFormat
                  value={price}
                  displayType="text"
                  prefix="$"
                  thousandSeparator
                  decimalScale={0}
                />
              </Text>
              {(hasDiscount && precioTachado > 0) && (
                <Typography
                  variant="body1"
                  component="p"
                  sx={{ textDecoration: 'line-through' }}
                >
                  <NumberFormat
                    value={precioTachado}
                    displayType="text"
                    prefix="$"
                    thousandSeparator
                    decimalScale={0}
                  />
                </Typography>
              )}
            </Grid>

            <Grid size={12}>
              {showHitchFrom && (
                hitchFrom({ hitchAmount: price * 0.20 })
              )}
            </Grid>

            <Grid
              size={12}
              mt={1}
              container
            >
              {
                consignaExist && (
                  <Grid size={7}>
                    <Typography
                      variant="body1"
                      sx={sx.sellerType}
                    >
                      <BranchIcon />
                      {isProximo
                        ? f({ id: 'SRP_CARD_CLIKAUTO_LOCATION' })
                        : vehicle?.easy
                          ? (consignaVirtual
                            ? f({ id: isAutocom ? 'VDP_CLIKAUTO_EASY_CONSIGNACION_AUTOCOM' : 'VDP_CLIKAUTO_EASY_CONSIGNACION_VIRTUAL' })
                            : f({ id: 'VDP_CLIKAUTO_EASY_CONSIGNACION_FISICA' }))
                          : getBranchName(vehicle?.seller?.branch?.name)}
                    </Typography>
                  </Grid>
                )
              }
              {state && (
                <Grid size={5}>
                  <Typography
                    variant="body1"
                    sx={sx.locationText}
                  >
                    <LocationIcon />
                    {formatStateToShortName(state)}
                  </Typography>
                </Grid>
              )}
            </Grid>

          </Grid>

        </Grid>
      </Paper>
    </CardLink>
  )
}

export default memo(VehicleMobileCardVariant)
